import { whatsappCanSend, whatsappStatusLabel } from '@crm/shared';
import { Badge, Button, cn, Input, Label, Spinner } from '@crm/ui';
import {
  AlertTriangle,
  Check,
  ExternalLink,
  MessageSquare,
  Pencil,
  RefreshCw,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { ConfirmDialog } from '@/components';
import { toErrorMessage } from '@/modules/auth/utils';
import { useLinkWhatsappAccount, useRefreshWhatsapp } from '../hooks';
import type { Unit } from '../types';

/**
 * O caminho que o assinante percorre na Twilio antes de voltar aqui. Sao
 * links, nao instrucoes decoradas: cada um abre exatamente a tela do passo.
 */
const PASSOS_TWILIO = [
  { texto: 'Criar a conta', url: 'https://www.twilio.com/try-twilio' },
  {
    texto: 'Comprar um numero dedicado',
    url: 'https://console.twilio.com/us1/develop/phone-numbers/manage/search',
  },
  {
    texto: 'Registrar o WhatsApp do numero',
    url: 'https://console.twilio.com/us1/develop/sms/senders/whatsapp-senders',
  },
] as const;

/** A cor acompanha o estado: verde so quando a mensagem sai de fato. */
const TOM: Record<string, string> = {
  ok: 'border-emerald-500/50 text-emerald-700 dark:text-emerald-400',
  wait: 'border-amber-500/50 text-amber-700 dark:text-amber-400',
  bad: 'border-destructive/50 text-destructive',
  none: '',
};

const CIRCULO: Record<string, string> = {
  ok: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  wait: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  bad: 'bg-destructive/10 text-destructive',
  none: 'bg-muted text-muted-foreground',
};

/**
 * O WhatsApp de uma unidade: o numero, o estado em que ele esta e a troca no
 * proprio lugar - sem abrir modal por cima de modal.
 *
 * O estado vem de quem entrega a mensagem, nao daqui: um numero salvo pode
 * estar aguardando verificacao e nao enviar nada, e escrever "Conectado" nesse
 * caso seria mentir para quem confia na tela.
 *
 * Vive fora da tela porque aparece em dois lugares - a aba da unidade e a
 * pagina que reune todas as filiais.
 */
export function WhatsappConnect({ unit, className }: { unit: Unit; className?: string }) {
  const vinculo = useLinkWhatsappAccount();
  const refresh = useRefreshWhatsapp();
  const [editando, setEditando] = useState(false);
  const [numero, setNumero] = useState(unit.whatsappFrom ?? '');
  const [accountSid, setAccountSid] = useState('');
  const [authToken, setAuthToken] = useState('');
  /** O numero esperando confirmacao - conectar tem consequencia no celular. */
  const [confirmando, setConfirmando] = useState<string | null>(null);

  const completo = Boolean(accountSid.trim() && authToken.trim() && numero.trim());
  const vincula = () => setConfirmando(numero);

  const conectado = Boolean(unit.whatsappFrom);
  // Numero salvo e sem estado nenhum quer dizer que ele nao foi encontrado
  // como remetente - diferente de nao ter numero.
  const estado =
    conectado && !unit.whatsappStatus
      ? { label: 'Numero nao habilitado', tone: 'bad' }
      : whatsappStatusLabel(unit.whatsappStatus);
  const enviando = whatsappCanSend(unit.whatsappStatus);
  const campoId = `whatsapp-${unit.id}`;
  const erro = vinculo.error ?? refresh.error;

  if (editando) {
    return (
      <div className={cn('space-y-3', className)}>
        {/* O aviso fica a vista antes de digitar, nao escondido no rodape:
              conectar um numero que ja tem WhatsApp derruba o aplicativo
              nele, e quem descobre depois perde o numero do salao. */}
        <div className="flex gap-2 rounded-card border border-amber-500/40 bg-amber-500/10 px-3 py-2.5">
          <AlertTriangle
            className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400"
            aria-hidden
          />
          <p className="text-xs text-amber-900 dark:text-amber-200">
            <strong className="font-semibold">Use um numero dedicado ao atendimento.</strong> Se
            este numero ja tiver WhatsApp instalado, o aplicativo deixa de funcionar nele e{' '}
            <strong className="font-semibold">
              as conversas e o historico daquele WhatsApp sao perdidos
            </strong>
            . A partir daqui o numero passa a ser usado apenas pelo sistema.
          </p>
        </div>

        {/* Passo 1: o cadastro do remetente acontece fora daqui, no painel
            da Twilio - nao temos como fazer por quem assina. O que da para
            fazer e nao deixar ninguem procurando onde clicar. */}
        <div className="space-y-2 rounded-card border border-border p-3">
          <p className="text-sm font-medium">1. Registre o numero na Twilio</p>
          <ol className="space-y-2 text-xs text-muted-foreground">
            {PASSOS_TWILIO.map((passo, indice) => (
              <li key={passo.url} className="flex flex-wrap items-center gap-1.5">
                <span className="text-muted-foreground/70">{indice + 1}.</span>
                {passo.texto}
                <a
                  href={passo.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
                >
                  abrir
                  <ExternalLink className="h-3 w-3" />
                </a>
              </li>
            ))}
          </ol>
        </div>

        <div className="space-y-3 rounded-card border border-border p-3">
          <p className="text-sm font-medium">2. Cole os dados da sua conta</p>

          <div className="grid gap-2 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor={`${campoId}-sid`} className="text-xs font-normal">
                Account SID
              </Label>
              <Input
                id={`${campoId}-sid`}
                autoFocus
                placeholder="AC00000000000000000000000000000000"
                value={accountSid}
                onChange={(event) => setAccountSid(event.target.value)}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor={`${campoId}-token`} className="text-xs font-normal">
                Auth Token
              </Label>
              <Input
                id={`${campoId}-token`}
                type="password"
                placeholder="••••••••"
                value={authToken}
                onChange={(event) => setAuthToken(event.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor={campoId} className="text-xs font-normal">
              Numero do WhatsApp
            </Label>
            <Input
              id={campoId}
              type="tel"
              placeholder="+1 914 873-5620"
              className="w-full sm:w-56"
              value={numero}
              onChange={(event) => setNumero(event.target.value)}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" disabled={!completo || vinculo.isPending} onClick={vincula}>
              {vinculo.isPending ? <Spinner /> : <Check className="h-4 w-4" />}
              Conectar
            </Button>

            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setNumero(unit.whatsappFrom ?? '');
                setEditando(false);
              }}
            >
              <X className="h-4 w-4" />
              Cancelar
            </Button>
          </div>

          <p className="text-xs text-muted-foreground">
            O Account SID e o Auth Token ficam na primeira tela do painel da Twilio. Guardamos os
            dois para poder enviar as mensagens em nome desta unidade.
          </p>

          {/* Numero recusado por ja ter conta ganha destaque: e o unico erro
              aqui que muda o que a pessoa tem de fazer - arrumar outro numero. */}
          {erro ? (
            <div className="flex gap-2 rounded-card border border-destructive/40 bg-destructive/10 px-3 py-2.5">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden />
              <p className="text-sm text-destructive">{toErrorMessage(erro)}</p>
            </div>
          ) : null}
        </div>

        <ConfirmDialog
          open={confirmando !== null}
          title="Conectar este numero?"
          message={
            <div className="space-y-3">
              <p className="text-sm">
                O numero <span className="font-semibold">{confirmando}</span> passa a ser usado
                apenas pelo sistema.
              </p>

              {/* O que se perde vem primeiro e em destaque: quem le rapido tem
                  de sair sabendo que nao ha volta sem migracao. */}
              <div className="flex gap-2 rounded-card border border-destructive/40 bg-destructive/10 px-3 py-2.5">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden />
                <p className="text-sm text-destructive">
                  Se este numero ja tiver WhatsApp ativo,{' '}
                  <strong className="font-semibold">
                    as conversas e todo o historico de mensagens serao perdidos
                  </strong>{' '}
                  e o aplicativo deixara de funcionar nele.
                </p>
              </div>

              <p className="text-sm text-muted-foreground">
                Voltar atras depois exige migrar o numero de novo.
              </p>
            </div>
          }
          confirmLabel="Conectar"
          loading={vinculo.isPending}
          onClose={() => setConfirmando(null)}
          onConfirm={() => {
            const phone = confirmando ?? '';
            setConfirmando(null);
            vinculo.mutate(
              {
                id: unit.id,
                accountSid: accountSid.trim(),
                authToken: authToken.trim(),
                phone,
              },
              { onSuccess: () => setEditando(false) },
            );
          }}
        />
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span
            className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
              CIRCULO[estado.tone],
            )}
          >
            <MessageSquare className="h-5 w-5" />
          </span>

          {conectado ? (
            <div className="min-w-0">
              <p className="truncate font-medium">{unit.whatsappFrom}</p>
              <Badge variant="outline" className={cn('mt-0.5', TOM[estado.tone])}>
                {estado.label}
              </Badge>
            </div>
          ) : (
            <p className="min-w-0 text-sm text-muted-foreground">
              Esta unidade ainda nao envia mensagem para o cliente.
            </p>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {/* Enquanto o numero nao esta liberado, o que resolve e reconsultar:
              a aprovacao acontece fora daqui e chega sem avisar. */}
          {conectado && !enviando ? (
            <Button
              variant="ghost"
              disabled={refresh.isPending}
              onClick={() => refresh.mutate(unit.id)}
            >
              {refresh.isPending ? <Spinner /> : <RefreshCw className="h-4 w-4" />}
              Atualizar
            </Button>
          ) : null}

          <Button variant={conectado ? 'outline' : 'default'} onClick={() => setEditando(true)}>
            {conectado ? <Pencil className="h-4 w-4" /> : <MessageSquare className="h-4 w-4" />}
            {conectado ? 'Trocar' : 'Conectar WhatsApp'}
          </Button>
        </div>
      </div>

      {conectado && !enviando ? (
        <p className="mt-2 text-xs text-muted-foreground">
          {unit.whatsappStatus
            ? 'Enquanto estiver assim, a mensagem nao sai. A liberacao costuma levar algumas horas.'
            : 'Este numero ainda nao esta habilitado a enviar. Conclua a habilitacao para as mensagens sairem.'}
        </p>
      ) : null}
    </div>
  );
}
